import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';
import SEOMeta from '../../components/SEOMeta';
import ThemeToggle from '../../components/ThemeToggle';
import { useDarkMode } from '../../hooks/useDarkMode';

const sections = [
  { id: 'sec-1', label: 'Definições' },
  { id: 'sec-2', label: 'Natureza Jurídica da Relação' },
  { id: 'sec-3', label: 'Cadastro e Elegibilidade' },
  { id: 'sec-4', label: 'Cupom de Desconto' },
  { id: 'sec-5', label: 'Comissão' },
  { id: 'sec-6', label: 'Apuração e Pagamento' },
  { id: 'sec-7', label: 'Estornos, Chargebacks e Refações' },
  { id: 'sec-8', label: 'Conduta do Afiliado' },
  { id: 'sec-9', label: 'Proteção de Dados (LGPD)' },
  { id: 'sec-10', label: 'Propriedade Intelectual' },
  { id: 'sec-11', label: 'Suspensão e Descredenciamento' },
  { id: 'sec-12', label: 'Encerramento por Iniciativa do Afiliado' },
  { id: 'sec-13', label: 'Alterações nos Termos' },
  { id: 'sec-14', label: 'Disposições Gerais' },
  { id: 'sec-15', label: 'Foro e Legislação Aplicável' },
];

export default function AfiliadosTermosPage() {
  const isDark = useDarkMode();
  const [activeSection, setActiveSection] = useState('sec-1');
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [mounted, setMounted] = useState(false);
  const articleRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 40);

      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      setProgress(total > 0 ? Math.min(1, Math.max(0, y / total)) : 0);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('term-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );
    const targets = articleRef.current?.querySelectorAll('.term-fade');
    targets?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <SEOMeta
        title="Termos do Programa de Afiliados | Inspira Músicas"
        description="Termos e condições do Programa de Afiliados Inspira Músicas. Natureza civil da parceria, comissão por venda confirmada, regras de cupom e pagamentos."
        path="/afiliados/termos"
      />

      {/* Page-scoped styles */}
      <style>{`
        .term-fade {
          opacity: 0;
          transform: translateY(18px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .term-visible {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        .term-numeral {
          font-family: 'DM Sans', sans-serif;
          font-weight: 800;
          letter-spacing: -0.05em;
          line-height: 0.85;
          font-feature-settings: 'tnum' on, 'lnum' on;
          background: linear-gradient(180deg, var(--gold) 0%, var(--amber-deep) 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .term-toc-item {
          position: relative;
          transition: color 0.2s ease, background 0.2s ease, padding 0.2s ease;
        }
        .term-toc-item.active {
          padding-left: 14px !important;
        }
        .term-toc-item.active::before {
          content: '';
          position: absolute;
          left: 4px;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 14px;
          border-radius: 2px;
          background: var(--gold);
        }
        .term-link-underline {
          position: relative;
          color: var(--gold);
          text-decoration: none;
          font-weight: 600;
        }
        .term-link-underline::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: -2px;
          height: 1.5px;
          background: var(--gold);
          opacity: 0.35;
          transition: opacity 0.2s;
        }
        .term-link-underline:hover::after { opacity: 1; }

        @media (prefers-reduced-motion: reduce) {
          .term-fade { opacity: 1; transform: none; transition: none; }
        }
      `}</style>

      <div style={{ background: 'var(--cream)', minHeight: '100vh', position: 'relative', overflow: 'clip' }}>

        {/* Reading-progress bar */}
        <div
          aria-hidden
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            zIndex: 60,
            background: 'transparent',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progress * 100}%`,
              background: 'linear-gradient(90deg, var(--gold) 0%, var(--amber-deep) 100%)',
              transition: 'width 0.1s linear',
            }}
          />
        </div>

        {/* Atmospheric blobs (homepage Hero pattern) */}
        <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
          <div style={{ position: 'absolute', top: -200, right: -80, width: 620, height: 620, borderRadius: '50%', background: 'radial-gradient(ellipse at 40% 40%, rgba(255,145,77,0.18) 0%, rgba(255,145,77,0.06) 45%, transparent 70%)', filter: 'blur(60px)' }} />
          <div style={{ position: 'absolute', top: '18%', left: -160, width: 460, height: 460, borderRadius: '50%', background: 'radial-gradient(ellipse at 60% 50%, rgba(255,145,77,0.13) 0%, rgba(255,176,122,0.05) 50%, transparent 70%)', filter: 'blur(70px)' }} />
          <div style={{ position: 'absolute', top: '55%', right: -100, width: 520, height: 520, borderRadius: '50%', background: 'radial-gradient(ellipse at 50% 50%, rgba(255,145,77,0.10) 0%, transparent 65%)', filter: 'blur(80px)' }} />
          <div style={{ position: 'absolute', bottom: -120, left: '20%', width: 600, height: 320, borderRadius: '50%', background: 'radial-gradient(ellipse at 50% 30%, rgba(255,145,77,0.09) 0%, transparent 65%)', filter: 'blur(80px)' }} />
        </div>

        {/* Header — logo size aligned with homepage */}
        <header
          className={`fixed top-0 left-0 w-full z-50 transition-all duration-300`}
          style={{
            background: scrolled ? 'rgba(255,255,255,0.78)' : 'transparent',
            backdropFilter: scrolled ? 'blur(14px)' : 'none',
            WebkitBackdropFilter: scrolled ? 'blur(14px)' : 'none',
            borderBottom: scrolled ? '1px solid var(--border-soft)' : 'none',
            boxShadow: scrolled ? '0 1px 24px rgba(0,0,0,0.04)' : 'none',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between" style={{ height: scrolled ? 76 : 96, transition: 'height 0.3s ease' }}>
            <Link to="/" className="flex items-center cursor-pointer" style={{ overflow: 'visible' }}>
              <img
                src={isDark ? '/logo-dark.png' : '/logo-light.png'}
                alt="Inspira Músicas"
                className="w-auto object-contain"
                style={{ height: scrolled ? 44 : 52, transition: 'height 0.3s ease' }}
              />
            </Link>

            <Link
              to="/afiliados"
              className="hidden md:flex items-center gap-2 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
              style={{ color: 'var(--earth)', fontFamily: "'DM Sans', sans-serif" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--gold)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--earth)')}
            >
              <i className="ri-arrow-left-line text-base" />
              Programa de Afiliados
            </Link>

            <div className="flex items-center gap-3">
              <ThemeToggle />
              <Link
                to="/afiliados"
                className="px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{
                  background: 'var(--gold)',
                  color: '#fff',
                  fontFamily: "'DM Sans', sans-serif",
                  boxShadow: '0 2px 12px rgba(200,131,42,0.25)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--gold-hover)';
                  e.currentTarget.style.boxShadow = '0 6px 22px rgba(200,131,42,0.40)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--gold)';
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(200,131,42,0.25)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Quero ser afiliado
              </Link>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section style={{ position: 'relative', zIndex: 2, paddingTop: 140 }}>
          <div className="max-w-7xl mx-auto px-4 md:px-8 pb-16 md:pb-20">

            {/* Eyebrow */}
            <div
              className="flex items-center gap-3 mb-7"
              style={{
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(12px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
              }}
            >
              <span style={{ width: 32, height: 1.5, background: 'var(--gold)' }} />
              <span
                className="text-xs uppercase font-semibold"
                style={{
                  letterSpacing: '0.22em',
                  color: 'var(--amber-deep)',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Documento Legal · Inspira Músicas
              </span>
            </div>

            {/* Title */}
            <h1
              className="mb-8"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 'clamp(40px, 6.5vw, 84px)',
                fontWeight: 800,
                lineHeight: 1.02,
                letterSpacing: '-0.035em',
                color: 'var(--heading-ink)',
                maxWidth: 980,
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(24px)',
                transition: 'opacity 0.7s ease 0.05s, transform 0.7s ease 0.05s',
              }}
            >
              Termos de adesão ao{' '}
              <span style={{ color: 'var(--gold)', position: 'relative', display: 'inline-block' }}>
                Programa de Afiliados
                <svg
                  viewBox="0 0 240 12"
                  preserveAspectRatio="none"
                  aria-hidden
                  style={{ position: 'absolute', bottom: -6, left: 0, width: '100%', height: 10, overflow: 'visible' }}
                >
                  <path
                    d="M2,7 Q40,2 80,6 Q140,11 200,4 Q220,2 238,6"
                    stroke="var(--gold)"
                    strokeWidth="2.5"
                    fill="none"
                    strokeLinecap="round"
                    opacity="0.55"
                  />
                </svg>
              </span>
              .
            </h1>

            {/* Lede */}
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 'clamp(16px, 1.6vw, 19px)',
                fontWeight: 300,
                lineHeight: 1.55,
                color: 'var(--text-strong)',
                maxWidth: 720,
                marginBottom: 40,
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.7s ease 0.15s, transform 0.7s ease 0.15s',
              }}
            >
              As regras da parceria comercial autônoma entre você e a <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>AUTOMAT&nbsp;TECNOLOGIA&nbsp;DA&nbsp;INFORMACAO&nbsp;LTDA</strong>. Lê com calma — vale tanto pra você quanto pra gente.
            </p>

            {/* Meta strip */}
            <div
              className="flex flex-wrap items-center gap-x-8 gap-y-3 mb-2"
              style={{
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.7s ease 0.25s, transform 0.7s ease 0.25s',
              }}
            >
              <Meta icon="ri-calendar-event-line" label="Atualizado em" value="5 de maio de 2026" />
              <Meta icon="ri-bookmark-3-line" label="Versão" value="1.1" />
              <Meta icon="ri-shield-check-line" label="Vigência" value="Imediata após cadastro" />
            </div>
          </div>
        </section>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-24" style={{ position: 'relative', zIndex: 2 }}>
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">

            {/* Sidebar TOC */}
            <aside className="hidden lg:block w-72 shrink-0">
              <div
                className="sticky top-28 rounded-3xl p-7"
                style={{
                  background: 'var(--warm-white)',
                  border: '1px solid var(--border-soft)',
                  boxShadow: '0 1px 0 rgba(0,0,0,0.02), 0 24px 60px -32px rgba(15,15,26,0.18)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <div className="flex items-center gap-2 mb-5">
                  <i className="ri-book-mark-line" style={{ color: 'var(--gold)', fontSize: 16 }} />
                  <p className="text-[11px] font-bold uppercase" style={{ letterSpacing: '0.2em', color: 'var(--amber-deep)', fontFamily: "'DM Sans', sans-serif" }}>
                    Sumário
                  </p>
                </div>
                <ul className="flex flex-col gap-0.5">
                  {sections.map((sec, i) => (
                    <li key={sec.id}>
                      <button
                        onClick={() => scrollToSection(sec.id)}
                        className={`term-toc-item w-full text-left py-2 px-2 rounded-lg cursor-pointer ${activeSection === sec.id ? 'active' : ''}`}
                        style={{
                          color: activeSection === sec.id ? 'var(--gold)' : 'var(--earth-light)',
                          fontFamily: "'DM Sans', sans-serif",
                          fontWeight: activeSection === sec.id ? 600 : 400,
                          fontSize: 12.5,
                          lineHeight: 1.45,
                          display: 'flex',
                          gap: 10,
                          alignItems: 'baseline',
                        }}
                      >
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: activeSection === sec.id ? 'var(--gold)' : 'var(--text-soft)',
                            fontFamily: "'DM Sans', sans-serif",
                            minWidth: 18,
                            fontFeatureSettings: '"tnum" on',
                          }}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span>{sec.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>

                <div className="mt-7 pt-5" style={{ borderTop: '1px dashed var(--border-soft)' }}>
                  <p className="text-[11px] font-bold uppercase mb-3" style={{ letterSpacing: '0.2em', color: 'var(--amber-deep)', fontFamily: "'DM Sans', sans-serif" }}>
                    Outros documentos
                  </p>
                  <div className="flex flex-col gap-1">
                    {[
                      { label: 'Termos de Serviço', href: '/termos-de-servico', icon: 'ri-file-text-line' },
                      { label: 'Política de Privacidade', href: '/politica-de-privacidade', icon: 'ri-shield-line' },
                      { label: 'Exclusão de Dados', href: '/exclusao-de-dados', icon: 'ri-delete-bin-line' },
                    ].map((page) => (
                      <Link
                        key={page.href}
                        to={page.href}
                        className="flex items-center gap-2 text-xs py-1.5 px-2 rounded-lg transition-all duration-200 cursor-pointer"
                        style={{ color: 'var(--earth-light)', fontFamily: "'DM Sans', sans-serif" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = 'var(--gold)';
                          e.currentTarget.style.background = 'var(--gold-pale)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = 'var(--earth-light)';
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <i className={`${page.icon} text-xs`} />
                        {page.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>

            {/* Main */}
            <article ref={articleRef} className="flex-1 min-w-0" style={{ maxWidth: 760 }}>

              {/* Preâmbulo */}
              <div
                className="term-fade rounded-3xl p-7 md:p-9 mb-14"
                style={{
                  background: 'linear-gradient(180deg, var(--warm-white) 0%, var(--cream) 100%)',
                  border: '1px solid var(--border-soft)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <div aria-hidden style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,145,77,0.18) 0%, transparent 65%)', filter: 'blur(28px)' }} />
                <span
                  aria-hidden
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 96,
                    lineHeight: 0.6,
                    color: 'var(--gold)',
                    opacity: 0.18,
                    fontWeight: 800,
                    display: 'block',
                    height: 36,
                  }}
                >
                  &ldquo;
                </span>
                <P>
                  Estes Termos regulam a participação no <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>Programa de Afiliados Inspira Músicas</strong>, operado por <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>AUTOMAT TECNOLOGIA DA INFORMACAO LTDA</strong>, inscrita no CNPJ sob o nº 59.640.197/0001-76, com sede na Rua Pais Leme, 215, Conj. 1713, Sala 01, Andar 16, Pinheiros, São Paulo/SP, CEP 05424-150 (doravante "Inspira Músicas" ou "nós").
                </P>
                <p className="text-sm md:text-base leading-relaxed" style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 400, margin: 0 }}>
                  Ao se cadastrar, você ("Afiliado") declara ter lido, compreendido e concordado com todas as cláusulas deste documento.
                </p>
              </div>

              {/* Section 1 */}
              <Section id="sec-1" number="01" title="Definições">
                <P>Para os fins destes Termos, considera-se:</P>
                <DefList items={[
                  { term: 'Programa de Afiliados', def: 'programa de divulgação e indicação que permite ao Afiliado promover o serviço Inspira Músicas em troca de comissão por venda concretizada e não reembolsada.' },
                  { term: 'Cupom', def: 'código de desconto exclusivo, atribuído individualmente a cada Afiliado, que concede desconto ao cliente final e identifica a indicação.' },
                  { term: 'Cliente Final', def: 'pessoa que adquire uma música personalizada da Inspira Músicas utilizando o Cupom do Afiliado.' },
                  { term: 'Venda Confirmada', def: 'pedido pago, entregue, e cuja janela de garantia de 7 dias foi encerrada sem solicitação de reembolso, refação total ou estorno.' },
                  { term: 'Comissão', def: 'valor devido ao Afiliado por cada Venda Confirmada, conforme definido na Cláusula 5.' },
                ]} />
              </Section>

              {/* Section 2 */}
              <Section id="sec-2" number="02" title="Natureza Jurídica da Relação">
                <Callout>Esta é a cláusula mais importante deste documento. Leia com atenção.</Callout>

                <SubTitle>2.1 Parceria comercial autônoma</SubTitle>
                <P>
                  O Programa de Afiliados é uma <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>parceria comercial autônoma de natureza estritamente civil</strong>, regida pelo Código Civil Brasileiro (Lei nº 10.406/2002), pelo Código de Defesa do Consumidor (quando aplicável), e pela legislação tributária pertinente.
                </P>

                <SubTitle>2.2 Ausência de vínculo</SubTitle>
                <P>
                  <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>Não há, em nenhuma hipótese, vínculo empregatício, societário, de prestação de serviços continuada, de representação comercial regulada pela Lei nº 4.886/65, de mandato, de franquia, ou de qualquer outra natureza que crie obrigações trabalhistas entre o Afiliado e a Inspira Músicas.</strong>
                </P>

                <SubTitle>2.3 Ausência dos requisitos do art. 3º da CLT</SubTitle>
                <P>Para deixar inequívoca a ausência dos requisitos do art. 3º da Consolidação das Leis do Trabalho (CLT), as partes reconhecem expressamente que:</P>
                <DefList items={[
                  { term: '(a) Não há subordinação', def: 'o Afiliado é integralmente livre para decidir quando, como, onde e com qual frequência divulgar o Cupom. A Inspira Músicas não dá ordens, não controla jornada, não estabelece metas obrigatórias, não exige comparecimento, não aplica advertências ou punições disciplinares, e não exerce qualquer forma de poder hierárquico sobre o Afiliado.' },
                  { term: '(b) Não há pessoalidade', def: 'o Afiliado pode delegar a operação de divulgação a terceiros (assistentes, agências, equipes de mídia), desde que não compartilhe credenciais de acesso à plataforma e responda pelos atos do terceiro.' },
                  { term: '(c) Não há habitualidade obrigatória', def: 'o Afiliado não tem obrigação de divulgar com qualquer frequência mínima. Pode permanecer inativo por tempo indeterminado, sem qualquer consequência. Não há cota mínima, meta obrigatória ou exigência de continuidade.' },
                  { term: '(d) Não há onerosidade típica de salário', def: 'a Comissão é exclusivamente vinculada a resultado (Venda Confirmada). Não há valor fixo mensal, ajuda de custo, gratificação, décimo terceiro, férias, ou qualquer outra rubrica de natureza salarial. Em meses sem Venda Confirmada, nada é devido ao Afiliado.' },
                ]} />

                <SubTitle>2.4 Não exclusividade</SubTitle>
                <P>
                  O Afiliado declara que <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>não é exclusivo</strong> da Inspira Músicas e está livre para divulgar produtos ou serviços de terceiros, inclusive concorrentes, sem qualquer restrição contratual.
                </P>

                <SubTitle>2.5 Obrigações fiscais e previdenciárias</SubTitle>
                <P>
                  O Afiliado é integralmente responsável por suas próprias obrigações fiscais, previdenciárias e trabalhistas decorrentes desta parceria. A Inspira Músicas não recolhe, retém ou repassa contribuições previdenciárias (INSS), FGTS, ou qualquer outro encargo de natureza trabalhista em favor do Afiliado.
                </P>

                <SubTitle>2.6 Competência judicial</SubTitle>
                <P>
                  Eventuais divergências entre as partes serão resolvidas exclusivamente pela <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>Justiça Comum</strong> (cível), sendo expressamente afastada a competência da Justiça do Trabalho.
                </P>
              </Section>

              {/* Section 3 */}
              <Section id="sec-3" number="03" title="Cadastro e Elegibilidade">
                <SubTitle>3.1 Requisitos de cadastro</SubTitle>
                <P>Para se cadastrar como Afiliado, é necessário:</P>
                <BulletList items={[
                  'Ter pelo menos 18 anos completos;',
                  'Possuir CPF válido e regular junto à Receita Federal;',
                  'Possuir conta bancária ou chave PIX em seu próprio nome;',
                  'Fornecer informações verdadeiras, completas e atualizadas no formulário de cadastro;',
                  'Concordar expressamente com estes Termos.',
                ]} />

                <SubTitle>3.2 Limite para Pessoa Física</SubTitle>
                <P>
                  O Afiliado pode atuar como Pessoa Física (PF) enquanto a soma de Comissões recebidas em um único mês civil não ultrapassar <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>R$ 1.000,00 (mil reais)</strong>.
                </P>

                <SubTitle>3.3 Obrigatoriedade de MEI ou PJ acima do limite</SubTitle>
                <P>
                  Caso o Afiliado supere ou esteja na iminência de superar o limite mensal de R$ 1.000,00, será obrigatória a apresentação de inscrição como <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>Microempreendedor Individual (MEI)</strong> ou Pessoa Jurídica (PJ) regularmente constituída, com emissão de Nota Fiscal de Serviços (descrição: "serviços de divulgação e marketing de afiliação") para cada pagamento de Comissão.
                </P>

                <SubTitle>3.4 Prazo de regularização</SubTitle>
                <P>
                  Se o Afiliado atingir o limite e não apresentar MEI/PJ no prazo de 30 dias após notificação, a Inspira Músicas poderá, a seu critério: (i) suspender o pagamento de Comissões adicionais até regularização, ou (ii) descredenciar o Afiliado do Programa.
                </P>

                <SubTitle>3.5 Recusa e descredenciamento</SubTitle>
                <P>
                  A Inspira Músicas se reserva o direito de recusar cadastros ou descredenciar Afiliados a qualquer momento, sem necessidade de justificativa, conforme Cláusula 11.
                </P>
              </Section>

              {/* Section 4 */}
              <Section id="sec-4" number="04" title="Cupom de Desconto">
                <SubTitle>4.1 Cupom exclusivo</SubTitle>
                <P>
                  Cada Afiliado receberá um Cupom exclusivo, vinculado ao seu cadastro, que concede desconto ao Cliente Final no momento da compra.
                </P>

                <SubTitle>4.2 Valor do desconto</SubTitle>
                <P>
                  O percentual ou valor do desconto associado ao Cupom é definido pela Inspira Músicas e pode variar ao longo do tempo. O Afiliado será informado do valor vigente no momento de seu cadastro e de eventuais alterações.
                </P>

                <SubTitle>4.3 Vedações de uso</SubTitle>
                <P>O Cupom é de uso exclusivo do Afiliado para divulgação aos Clientes Finais. <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>É vedado:</strong></P>
                <BulletList items={[
                  'Compartilhar o Cupom com outros Afiliados;',
                  'Publicar o Cupom em sites agregadores de cupons de desconto sem autorização prévia da Inspira Músicas;',
                  'Utilizar o Cupom em campanhas de mídia paga que façam concorrência direta com as campanhas oficiais da Inspira Músicas (incluindo, mas não limitado a, anúncios em palavras-chave da marca "Inspira Músicas" ou variações).',
                ]} />

                <SubTitle>4.4 Consequências do descumprimento</SubTitle>
                <P>
                  O descumprimento das vedações da Cláusula 4.3 poderá acarretar suspensão de Comissões, descredenciamento do Programa e, se aplicável, responsabilização civil pelos danos causados.
                </P>
              </Section>

              {/* Section 5 */}
              <Section id="sec-5" number="05" title="Comissão">
                <SubTitle>5.1 Valor da Comissão</SubTitle>
                <PriceTag value="30%" caption="sobre o valor pago pelo Cliente Final em cada Venda Confirmada" />
                <P>
                  A Comissão corresponde a <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>30% (trinta por cento) sobre o valor efetivamente pago pelo Cliente Final</strong>, já considerado o desconto de 10% concedido por meio do Cupom do Afiliado e quaisquer outras promoções aplicáveis no momento da compra.
                </P>

                <SubTitle>5.2 Múltiplas músicas no mesmo pedido</SubTitle>
                <P>
                  Caso o Cliente Final adquira mais de uma música em um mesmo pedido (upsell ou pacote), a Comissão de 30% incide sobre o <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>valor total pago no pedido</strong>, não havendo limite de itens por pedido para fins de cálculo.
                </P>

                <SubTitle>5.3 Quando a Comissão é devida</SubTitle>
                <P>
                  Somente sobre Vendas Confirmadas, conforme definição na Cláusula 1. Pedidos cancelados, estornados, reembolsados (parcial ou integralmente) ou refeitos por insatisfação do Cliente Final <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>não geram direito à Comissão</strong>.
                </P>

                <SubTitle>5.4 Reembolso parcial</SubTitle>
                <P>
                  Em caso de reembolso parcial, a Comissão é recalculada sobre o valor líquido efetivamente retido pela Inspira Músicas após o reembolso.
                </P>

                <SubTitle>5.5 Revisão do percentual</SubTitle>
                <P>
                  A Inspira Músicas se reserva o direito de revisar o percentual da Comissão a qualquer momento, mediante aviso prévio de 30 (trinta) dias ao Afiliado. As Comissões já apuradas no momento da revisão serão pagas conforme o percentual vigente na data da venda.
                </P>

                <SubTitle>5.6 Despesas do Afiliado</SubTitle>
                <P>
                  A Comissão <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>não inclui</strong> quaisquer reembolsos por despesas que o Afiliado venha a ter com sua atividade de divulgação (ex: custos de mídia paga, produção de conteúdo, equipamentos). Tais despesas são de responsabilidade exclusiva do Afiliado.
                </P>
              </Section>

              {/* Section 6 */}
              <Section id="sec-6" number="06" title="Apuração e Pagamento">
                <SubTitle>6.1 Ciclo de apuração</SubTitle>
                <P>
                  As Comissões são apuradas mensalmente, considerando todas as Vendas Confirmadas entre o primeiro e o último dia do mês civil.
                </P>

                <SubTitle>6.2 Janela de carência</SubTitle>
                <P>
                  Uma venda só é considerada "Confirmada" e apta a gerar Comissão <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>8 (oito) dias após a data do pagamento pelo Cliente Final</strong>, prazo necessário para encerramento da janela de garantia de 7 dias oferecida pela Inspira Músicas. Vendas pagas nos últimos dias do mês cuja janela de garantia se encerre no mês seguinte serão computadas no ciclo de apuração do mês em que a janela se encerrar.
                </P>

                <SubTitle>6.3 Data de pagamento</SubTitle>
                <P>
                  As Comissões apuradas em determinado mês são pagas até o <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>dia 10 (dez) do mês subsequente</strong>, por PIX, na chave informada pelo Afiliado em seu cadastro.
                </P>

                <SubTitle>6.4 Comprovação</SubTitle>
                <P>
                  O Afiliado terá acesso a um portal próprio (
                  <Link to="/afiliados/portal" className="term-link-underline">/afiliados/portal</Link>
                  ) para acompanhar suas vendas, status de cada pedido e Comissões a receber. Em caso de divergência, o Afiliado deverá comunicar à Inspira Músicas no prazo de 30 dias após o pagamento, sob pena de preclusão do direito de questionamento.
                </P>

                <SubTitle>6.5 Retenções fiscais (PF)</SubTitle>
                <P>
                  Se o Afiliado atuar como Pessoa Física e a Inspira Músicas for legalmente obrigada a reter Imposto de Renda na Fonte ou outras contribuições, tais retenções serão aplicadas sobre o valor bruto da Comissão e o Afiliado receberá o valor líquido, com fornecimento do comprovante de retenção.
                </P>

                <SubTitle>6.6 Tributação (MEI/PJ)</SubTitle>
                <P>
                  Se o Afiliado atuar como MEI ou PJ, deverá emitir Nota Fiscal de Serviços para cada pagamento de Comissão. O pagamento somente será efetivado após o recebimento da nota fiscal regular pela Inspira Músicas.
                </P>

                <SubTitle>6.7 Valor mínimo</SubTitle>
                <P>
                  Não há valor mínimo acumulado para pagamento. Mesmo Comissões originárias de uma única Venda Confirmada serão pagas no ciclo correspondente.
                </P>

                <SubTitle>6.8 Inatividade</SubTitle>
                <P>
                  Caso o Afiliado permaneça sem nenhuma Venda Confirmada por 12 meses consecutivos, a Inspira Músicas poderá descredenciá-lo automaticamente, sem que isso configure rescisão indenizável.
                </P>
              </Section>

              {/* Section 7 */}
              <Section id="sec-7" number="07" title="Estornos, Chargebacks e Refações">
                <SubTitle>7.1 Estorno antes do pagamento</SubTitle>
                <P>
                  Caso uma venda seja estornada, reembolsada ou objeto de chargeback <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>antes</strong> do pagamento da Comissão correspondente, a venda deixará de ser considerada Venda Confirmada e a Comissão não será paga.
                </P>

                <SubTitle>7.2 Estorno após o pagamento</SubTitle>
                <P>
                  Caso uma venda seja estornada, reembolsada ou objeto de chargeback <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>após</strong> o pagamento da Comissão (em casos excepcionais, como fraude detectada tardiamente ou disputa bancária prolongada), o valor pago será deduzido das Comissões devidas ao Afiliado em ciclos futuros. Caso não haja Comissões futuras suficientes para compensar, o Afiliado se compromete a restituir o valor à Inspira Músicas no prazo de 15 dias.
                </P>

                <SubTitle>7.3 Refações</SubTitle>
                <P>
                  Refações solicitadas pelo Cliente Final que não impliquem reembolso (ou seja, refação simples por insatisfação com a primeira versão da música, mantendo o pedido pago) <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>não afetam</strong> o direito à Comissão.
                </P>

                <SubTitle>7.4 Fraude e uso indevido</SubTitle>
                <P>
                  A Inspira Músicas se reserva o direito de não pagar Comissões em casos de evidência de fraude, manipulação de pedidos, uso indevido do Cupom ou qualquer outra prática que viole estes Termos.
                </P>
              </Section>

              {/* Section 8 */}
              <Section id="sec-8" number="08" title="Conduta do Afiliado">
                <SubTitle>8.1 Práticas vedadas</SubTitle>
                <P>É expressamente proibido ao Afiliado:</P>
                <BulletList items={[
                  'Realizar spam em qualquer plataforma (e-mail em massa não solicitado, mensagens automáticas em redes sociais, comentários repetitivos);',
                  'Usar a marca "Inspira Músicas" ou variações em domínios de internet, perfis de redes sociais, ou anúncios pagos sem autorização escrita prévia;',
                  'Se passar por funcionário, representante oficial ou pelo próprio nome da Inspira Músicas;',
                  'Fazer afirmações falsas, enganosas ou que possam induzir o Cliente Final a erro sobre o produto, prazos, garantia ou características do serviço;',
                  'Mencionar ou sugerir que a música é gerada por inteligência artificial ou tecnologia automatizada (a comunicação institucional da Inspira Músicas não enfatiza esse aspecto, e o Afiliado deve seguir o mesmo padrão);',
                  'Utilizar imagens, vídeos, áudios ou textos protegidos por direitos autorais de terceiros sem autorização;',
                  'Veicular conteúdo discriminatório, ofensivo, sexualmente explícito, violento ou ilegal em peças de divulgação;',
                  'Realizar anúncios pagos em palavras-chave da marca (ex: comprar palavra "inspira músicas" no Google Ads ou Meta Ads) sem autorização escrita prévia;',
                  'Direcionar tráfego para páginas falsas ou fraudulentas que se passem pela Inspira Músicas.',
                ]} />

                <SubTitle>8.2 Materiais de divulgação</SubTitle>
                <P>
                  A Inspira Músicas pode fornecer materiais oficiais (textos, imagens, vídeos) para uso do Afiliado. O Afiliado é livre para criar materiais próprios, desde que respeite as regras desta Cláusula 8 e a Cláusula 10 (Propriedade Intelectual).
                </P>

                <SubTitle>8.3 Responsabilidade pelo conteúdo</SubTitle>
                <P>
                  O Afiliado é integralmente responsável pelo conteúdo que publicar em sua atividade de divulgação. A Inspira Músicas não revisa previamente o material publicado pelo Afiliado e não responde por qualquer dano que tal material possa causar a terceiros.
                </P>
              </Section>

              {/* Section 9 */}
              <Section id="sec-9" number="09" title="Proteção de Dados (LGPD)">
                <SubTitle>9.1 Compromisso com a LGPD</SubTitle>
                <P>
                  O Afiliado se compromete a respeitar a Lei Geral de Proteção de Dados (Lei nº 13.709/2018) em sua atividade de divulgação.
                </P>

                <SubTitle>9.2 Dados dos Clientes Finais</SubTitle>
                <P>
                  O Afiliado <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>não receberá</strong> dados pessoais dos Clientes Finais que utilizarem seu Cupom. Os dados estatísticos disponibilizados no portal do Afiliado são agregados e não permitem identificação individual.
                </P>

                <SubTitle>9.3 Coleta própria de leads</SubTitle>
                <P>
                  Caso o Afiliado venha a coletar dados de potenciais clientes em sua atividade (ex: leads em formulários próprios), será considerado controlador desses dados e responsável pelo cumprimento integral da LGPD em relação a essa coleta.
                </P>

                <SubTitle>9.4 Dados do Afiliado</SubTitle>
                <P>
                  Os dados pessoais do próprio Afiliado (nome, CPF, e-mail, telefone, dados bancários) são tratados pela Inspira Músicas conforme sua{' '}
                  <Link to="/politica-de-privacidade" className="term-link-underline">Política de Privacidade</Link>
                  , com finalidade exclusiva de operação do Programa de Afiliados, pagamento de Comissões e cumprimento de obrigações legais.
                </P>
              </Section>

              {/* Section 10 */}
              <Section id="sec-10" number="10" title="Propriedade Intelectual">
                <SubTitle>10.1 Titularidade</SubTitle>
                <P>
                  A marca "Inspira Músicas", o logotipo, os elementos de identidade visual, os textos do site, os materiais oficiais de divulgação e qualquer outro conteúdo da Inspira Músicas são de propriedade exclusiva da AUTOMAT TECNOLOGIA DA INFORMACAO LTDA.
                </P>

                <SubTitle>10.2 Licença concedida</SubTitle>
                <P>
                  A Inspira Músicas concede ao Afiliado uma <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>licença limitada, não exclusiva, revogável e não transferível</strong> para utilizar a marca e os materiais oficiais exclusivamente na divulgação do Programa, durante a vigência do cadastro do Afiliado.
                </P>

                <SubTitle>10.3 Restrições da licença</SubTitle>
                <P>Esta licença <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>não autoriza</strong> o Afiliado a:</P>
                <BulletList items={[
                  'Registrar ou tentar registrar qualquer marca, domínio ou perfil contendo "Inspira" ou "Inspira Músicas";',
                  'Modificar substancialmente o logotipo ou a identidade visual da marca;',
                  'Utilizar a marca em produtos ou serviços que não sejam a divulgação do Programa de Afiliados.',
                ]} />

                <SubTitle>10.4 Revogação</SubTitle>
                <P>
                  A licença é revogada automaticamente em caso de descredenciamento do Afiliado, e o Afiliado deverá remover toda e qualquer menção à marca em suas publicações no prazo de 15 dias após o descredenciamento.
                </P>
              </Section>

              {/* Section 11 */}
              <Section id="sec-11" number="11" title="Suspensão e Descredenciamento">
                <SubTitle>11.1 Hipóteses de suspensão ou descredenciamento</SubTitle>
                <P>A Inspira Músicas pode, a seu exclusivo critério e a qualquer momento, suspender ou descredenciar o Afiliado nas seguintes hipóteses, entre outras:</P>
                <BulletList items={[
                  'Violação de qualquer cláusula destes Termos;',
                  'Indícios de fraude ou manipulação de pedidos;',
                  'Reclamações repetidas de Clientes Finais sobre práticas do Afiliado;',
                  'Inatividade superior a 12 meses;',
                  'Veiculação de conteúdo que viole leis brasileiras ou direitos de terceiros;',
                  'Recusa em apresentar MEI/PJ quando obrigatório (Cláusula 3.4).',
                ]} />

                <SubTitle>11.2 Encerramento do programa</SubTitle>
                <P>
                  A Inspira Músicas pode encerrar o Programa de Afiliados como um todo a qualquer momento, mediante aviso prévio de 30 (trinta) dias publicado em seu site oficial, sem que isso gere direito a qualquer indenização aos Afiliados.
                </P>

                <SubTitle>11.3 Comissões em aberto</SubTitle>
                <P>
                  Em caso de descredenciamento, as Comissões já apuradas e referentes a Vendas Confirmadas até a data do descredenciamento serão pagas no ciclo regular subsequente, salvo nas hipóteses de fraude ou violação grave dos Termos, em que tais valores poderão ser retidos para apuração.
                </P>
              </Section>

              {/* Section 12 */}
              <Section id="sec-12" number="12" title="Encerramento por Iniciativa do Afiliado">
                <SubTitle>12.1 Direito de encerrar</SubTitle>
                <P>
                  O Afiliado pode encerrar sua participação no Programa a qualquer momento, sem necessidade de justificativa e sem qualquer ônus.
                </P>

                <SubTitle>12.2 Como encerrar</SubTitle>
                <P>
                  Para encerrar, basta enviar e-mail para <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>contato@inspiramusicas.com.br</strong> solicitando o descredenciamento.
                </P>

                <SubTitle>12.3 Comissões pendentes</SubTitle>
                <P>
                  As Comissões já apuradas até a data do pedido de encerramento serão pagas no ciclo regular subsequente.
                </P>

                <SubTitle>12.4 Ausência de obrigações trabalhistas</SubTitle>
                <P>
                  <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>Não há aviso prévio obrigatório, multa rescisória, indenização por encerramento, verbas rescisórias, aviso prévio remunerado, ou qualquer outra obrigação de natureza trabalhista entre as partes</strong>, em decorrência do encerramento — reforçando o disposto na Cláusula 2.
                </P>
              </Section>

              {/* Section 13 */}
              <Section id="sec-13" number="13" title="Alterações nos Termos">
                <SubTitle>13.1 Direito de alteração</SubTitle>
                <P>
                  A Inspira Músicas pode alterar estes Termos a qualquer momento, mediante publicação da nova versão em <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>inspiramusicas.com.br/afiliados/termos</strong> e envio de aviso aos Afiliados cadastrados, com antecedência mínima de 15 dias.
                </P>

                <SubTitle>13.2 Discordância</SubTitle>
                <P>
                  Caso o Afiliado discorde das alterações, poderá encerrar sua participação no Programa conforme Cláusula 12. A continuidade no Programa após a entrada em vigor das alterações implica concordância com a nova versão.
                </P>
              </Section>

              {/* Section 14 */}
              <Section id="sec-14" number="14" title="Disposições Gerais">
                <SubTitle>14.1 Integralidade</SubTitle>
                <P>
                  Estes Termos representam o acordo integral entre as partes em relação ao Programa de Afiliados, prevalecendo sobre quaisquer comunicações, propostas ou acordos anteriores.
                </P>

                <SubTitle>14.2 Independência das cláusulas</SubTitle>
                <P>
                  Caso qualquer cláusula destes Termos seja considerada nula ou inexequível por decisão judicial, as demais cláusulas permanecem válidas e em pleno vigor.
                </P>

                <SubTitle>14.3 Tolerância</SubTitle>
                <P>
                  A não exigência, pela Inspira Músicas, do cumprimento de qualquer cláusula em momento específico não implica renúncia ao direito de exigi-la em momento posterior.
                </P>

                <SubTitle>14.4 Comunicações</SubTitle>
                <P>
                  Todas as comunicações relacionadas a estes Termos serão feitas por e-mail, no endereço cadastrado pelo Afiliado.
                </P>
              </Section>

              {/* Section 15 */}
              <Section id="sec-15" number="15" title="Foro e Legislação Aplicável" last>
                <SubTitle>15.1 Lei aplicável</SubTitle>
                <P>
                  Estes Termos são regidos pelas leis da República Federativa do Brasil, em especial pelo Código Civil (Lei nº 10.406/2002).
                </P>

                <SubTitle>15.2 Foro eleito</SubTitle>
                <P>
                  Fica eleito o <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>Foro da Comarca de São Paulo, Estado de São Paulo</strong>, para dirimir qualquer controvérsia oriunda destes Termos, com exclusão de qualquer outro, por mais privilegiado que seja.
                </P>

                <SubTitle>15.3 Reafirmação da natureza civil</SubTitle>
                <P>
                  As partes reafirmam, conforme Cláusula 2, que <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>a Justiça do Trabalho não é competente</strong> para processar e julgar litígios decorrentes desta relação, dada sua natureza estritamente civil e comercial.
                </P>
              </Section>

              {/* Acceptance card */}
              <div
                className="term-fade mt-16 rounded-3xl p-8 md:p-10 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, var(--earth-strong) 0%, var(--earth) 100%)',
                  color: '#fff',
                  boxShadow: '0 30px 80px -30px rgba(15,15,26,0.45)',
                }}
              >
                <div aria-hidden style={{ position: 'absolute', top: -80, right: -60, width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,145,77,0.32) 0%, transparent 65%)', filter: 'blur(40px)' }} />
                <div aria-hidden style={{ position: 'absolute', bottom: -60, left: -40, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,176,122,0.18) 0%, transparent 65%)', filter: 'blur(40px)' }} />

                <div className="relative">
                  <span
                    className="text-[11px] uppercase font-bold inline-flex items-center gap-2 mb-4"
                    style={{ letterSpacing: '0.22em', color: 'var(--gold-light)', fontFamily: "'DM Sans', sans-serif" }}
                  >
                    <span style={{ width: 24, height: 1.5, background: 'var(--gold-light)' }} />
                    Aceitação
                  </span>
                  <h3
                    className="text-2xl md:text-3xl mb-4"
                    style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15, color: '#fff' }}
                  >
                    Ao clicar em "Li e concordo" no momento do cadastro,<br className="hidden md:block" />
                    <span style={{ color: 'var(--gold-light)' }}>você firma vínculo jurídico válido</span> com a AUTOMAT.
                  </h3>
                  <p
                    className="text-sm md:text-base leading-relaxed"
                    style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, color: 'rgba(255,255,255,0.78)', maxWidth: 620 }}
                  >
                    Sua manifestação é expressa, integral e inequívoca em relação a todas as cláusulas deste documento.
                  </p>

                  <div className="mt-7 flex flex-wrap gap-3">
                    <Link
                      to="/afiliados"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all"
                      style={{ background: 'var(--gold)', color: '#fff', fontFamily: "'DM Sans', sans-serif", textDecoration: 'none', boxShadow: '0 4px 24px rgba(255,145,77,0.40)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gold-hover)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--gold)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                      Quero ser afiliado
                      <i className="ri-arrow-right-line" />
                    </Link>
                    <a
                      href="mailto:contato@inspiramusicas.com.br"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all"
                      style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.15)', fontFamily: "'DM Sans', sans-serif", textDecoration: 'none' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                    >
                      <i className="ri-mail-line" />
                      Tirar uma dúvida
                    </a>
                  </div>
                </div>
              </div>

              {/* Company colophon */}
              <div className="mt-12 pt-8" style={{ borderTop: '1px dashed var(--border-soft)' }}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  <div>
                    <p className="uppercase font-bold mb-2" style={{ letterSpacing: '0.2em', color: 'var(--amber-deep)', fontSize: 10 }}>Razão social</p>
                    <p style={{ color: 'var(--earth)', fontWeight: 600 }}>AUTOMAT TECNOLOGIA<br />DA INFORMACAO LTDA</p>
                    <p style={{ color: 'var(--text-soft)' }}>CNPJ 59.640.197/0001-76</p>
                  </div>
                  <div>
                    <p className="uppercase font-bold mb-2" style={{ letterSpacing: '0.2em', color: 'var(--amber-deep)', fontSize: 10 }}>Sede</p>
                    <p style={{ color: 'var(--text-strong)' }}>Rua Pais Leme, 215<br />Conj. 1713, Sala 01, Andar 16</p>
                    <p style={{ color: 'var(--text-soft)' }}>Pinheiros · São Paulo/SP · 05424-150</p>
                  </div>
                  <div>
                    <p className="uppercase font-bold mb-2" style={{ letterSpacing: '0.2em', color: 'var(--amber-deep)', fontSize: 10 }}>Contato</p>
                    <a href="mailto:contato@inspiramusicas.com.br" className="term-link-underline" style={{ fontSize: 12 }}>
                      contato@inspiramusicas.com.br
                    </a>
                  </div>
                </div>
              </div>

              {/* Back to top */}
              <div className="mt-10 text-center">
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
                  style={{ background: 'transparent', color: 'var(--earth)', border: '1.5px solid var(--border-soft)', fontFamily: "'DM Sans', sans-serif" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.color = 'var(--gold)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-soft)'; e.currentTarget.style.color = 'var(--earth)'; }}
                >
                  <i className="ri-arrow-up-line text-sm" />
                  Voltar ao topo
                </button>
              </div>

            </article>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────── */

function Section({
  id,
  number,
  title,
  children,
  last,
}: {
  id: string;
  number: string;
  title: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <section
      id={id}
      className="term-fade scroll-mt-32"
      style={{ marginBottom: last ? 0 : 64 }}
    >
      {/* Numeral + title row */}
      <div className="flex items-start gap-5 md:gap-7 mb-7">
        <span
          aria-hidden
          className="term-numeral shrink-0"
          style={{
            fontSize: 'clamp(56px, 8vw, 96px)',
            marginTop: -8,
          }}
        >
          {number}
        </span>
        <div style={{ flex: 1, paddingTop: 6 }}>
          <span
            className="text-[10px] uppercase font-bold mb-2 inline-block"
            style={{ letterSpacing: '0.2em', color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif" }}
          >
            Cláusula {number}
          </span>
          <h2
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 'clamp(24px, 3vw, 32px)',
              fontWeight: 800,
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              color: 'var(--heading-ink)',
              margin: 0,
            }}
          >
            {title}
          </h2>
        </div>
      </div>
      <div style={{ paddingLeft: 0 }}>
        {children}
      </div>
      {!last && (
        <div className="flex items-center justify-center gap-2 mt-12" aria-hidden>
          <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--border-soft)' }} />
          <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--gold)' }} />
          <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--border-soft)' }} />
        </div>
      )}
    </section>
  );
}

function SubTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3
      className="mt-7 mb-2"
      style={{
        fontSize: 15,
        fontWeight: 700,
        color: 'var(--earth)',
        fontFamily: "'DM Sans', sans-serif",
        letterSpacing: '-0.005em',
      }}
    >
      {children}
    </h3>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="mb-3"
      style={{
        fontSize: 15.5,
        lineHeight: 1.7,
        color: 'var(--text-strong)',
        fontFamily: "'DM Sans', sans-serif",
        fontWeight: 300,
      }}
    >
      {children}
    </p>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-2.5 my-4 ml-0">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span
            aria-hidden
            className="mt-2.5 shrink-0"
            style={{ width: 14, height: 1.5, background: 'var(--gold)', borderRadius: 1 }}
          />
          <span
            style={{
              fontSize: 15.5,
              lineHeight: 1.7,
              color: 'var(--text-strong)',
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 300,
            }}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function DefList({ items }: { items: { term: string; def: string }[] }) {
  return (
    <ul className="flex flex-col gap-3 my-4">
      {items.map((item) => (
        <li
          key={item.term}
          style={{
            paddingLeft: 18,
            borderLeft: '2px solid var(--gold)',
            paddingTop: 2,
            paddingBottom: 2,
          }}
        >
          <span
            style={{
              fontSize: 15.5,
              lineHeight: 1.7,
              color: 'var(--text-strong)',
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 300,
            }}
          >
            <strong style={{ color: 'var(--earth-strong)', fontWeight: 700 }}>{item.term}:</strong> {item.def}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="rounded-2xl px-5 py-4 mb-5 flex items-start gap-3"
      style={{
        background: 'linear-gradient(135deg, var(--gold-pale) 0%, rgba(255,176,122,0.22) 100%)',
        border: '1px solid var(--gold-border)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.6) inset',
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          background: 'var(--gold)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <i className="ri-alert-line" style={{ color: '#fff', fontSize: 16 }} />
      </div>
      <p
        className="leading-relaxed"
        style={{
          fontSize: 14.5,
          color: 'var(--earth-strong)',
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: 600,
          margin: 0,
          paddingTop: 4,
        }}
      >
        {children}
      </p>
    </div>
  );
}

function PriceTag({ value, caption }: { value: string; caption: string }) {
  return (
    <div
      className="my-4 inline-flex items-baseline gap-3 px-5 py-3 rounded-2xl"
      style={{
        background: 'linear-gradient(135deg, var(--gold-pale) 0%, rgba(255,176,122,0.18) 100%)',
        border: '1px solid var(--gold-border)',
      }}
    >
      <span
        style={{
          fontSize: 32,
          fontWeight: 800,
          color: 'var(--gold)',
          fontFamily: "'DM Sans', sans-serif",
          letterSpacing: '-0.025em',
          lineHeight: 1,
        }}
      >
        {value}
      </span>
      <span
        style={{
          fontSize: 13,
          color: 'var(--amber-deep)',
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: 500,
        }}
      >
        {caption}
      </span>
    </div>
  );
}

function Meta({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <i className={icon} style={{ color: 'var(--gold)', fontSize: 16 }} />
      <div>
        <p className="text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.18em', color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif", margin: 0 }}>
          {label}
        </p>
        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--earth)', fontFamily: "'DM Sans', sans-serif", margin: 0 }}>
          {value}
        </p>
      </div>
    </div>
  );
}
